import { useEffect, useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const PROJECT_PAUSE_REASONS = [
  "Awaiting client inputs",
  "Payment delay",
  "Strategic hold",
  "Capacity planning",
  "Campaign restructuring",
  "Other",
];

const PROJECT_ARCHIVE_REASONS = [
  "Contract ended",
  "Client requested closure",
  "Merged into another project",
  "Long-term inactive",
  "Other",
];

const currentMonthValue = () => {
  const now = new Date();
  const month = `${now.getMonth() + 1}`.padStart(2, "0");
  return `${now.getFullYear()}-${month}`;
};

export const ProjectLifecycleDialogs = ({
  testIdPrefix,
  pauseOpen,
  onPauseOpenChange,
  archiveOpen,
  onArchiveOpenChange,
  projectName,
  clientName,
  isSaving,
  onConfirmPause,
  onConfirmArchive,
}) => {
  const [pauseForm, setPauseForm] = useState({ reason: "", notes: "", resume_expected: "", effective_month: currentMonthValue() });
  const [archiveForm, setArchiveForm] = useState({ reason: "", confirm_name: "", effective_month: currentMonthValue() });

  useEffect(() => {
    if (pauseOpen) {
      setPauseForm({ reason: "", notes: "", resume_expected: "", effective_month: currentMonthValue() });
    }
  }, [pauseOpen]);

  useEffect(() => {
    if (archiveOpen) {
      setArchiveForm({ reason: "", confirm_name: "", effective_month: currentMonthValue() });
    }
  }, [archiveOpen]);

  const archiveNameMatched = useMemo(() => {
    const confirmValue = String(archiveForm.confirm_name || "").trim().toLowerCase();
    if (!archiveForm.reason || !confirmValue) return false;
    const validNames = [projectName, clientName]
      .map((value) => String(value || "").trim().toLowerCase())
      .filter(Boolean);
    return validNames.includes(confirmValue);
  }, [archiveForm.confirm_name, archiveForm.reason, clientName, projectName]);

  return (
    <>
      <Dialog open={pauseOpen} onOpenChange={onPauseOpenChange}>
        <DialogContent className="sm:max-w-[560px]" data-testid={`${testIdPrefix}-pause-modal`}>
          <DialogHeader>
            <DialogTitle data-testid={`${testIdPrefix}-pause-modal-title`}>Pause project</DialogTitle>
            <DialogDescription data-testid={`${testIdPrefix}-pause-modal-subtitle`}>
              {projectName || "Project"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="lifecycle-modal-impact-note pause" data-testid={`${testIdPrefix}-pause-modal-impact-note`}>
              <strong>What pausing does:</strong><br />
              Removes from active retainer total · Tasks frozen · Client portal goes read-only · Health score preserved · You can resume at any time.
            </div>

            <label className="field-label" data-testid={`${testIdPrefix}-pause-reason-label`}>Reason *</label>
            <select
              className="field-select"
              value={pauseForm.reason}
              onChange={(event) => setPauseForm((prev) => ({ ...prev, reason: event.target.value }))}
              data-testid={`${testIdPrefix}-pause-reason-select`}
            >
              <option value="">Select reason...</option>
              {PROJECT_PAUSE_REASONS.map((reason) => (
                <option key={reason} value={reason}>{reason}</option>
              ))}
            </select>

            <label className="field-label" data-testid={`${testIdPrefix}-pause-notes-label`}>Notes (optional)</label>
            <Textarea
              value={pauseForm.notes}
              onChange={(event) => setPauseForm((prev) => ({ ...prev, notes: event.target.value }))}
              placeholder="Any context for the pause..."
              data-testid={`${testIdPrefix}-pause-notes-input`}
            />

            <label className="field-label" data-testid={`${testIdPrefix}-pause-resume-date-label`}>Expected resume date (optional)</label>
            <Input
              type="date"
              value={pauseForm.resume_expected}
              onChange={(event) => setPauseForm((prev) => ({ ...prev, resume_expected: event.target.value }))}
              data-testid={`${testIdPrefix}-pause-resume-date-input`}
            />

            <label className="field-label" data-testid={`${testIdPrefix}-pause-effective-month-label`}>Apply in receivables from month *</label>
            <Input
              type="month"
              value={pauseForm.effective_month}
              onChange={(event) => setPauseForm((prev) => ({ ...prev, effective_month: event.target.value }))}
              data-testid={`${testIdPrefix}-pause-effective-month-input`}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onPauseOpenChange(false)} data-testid={`${testIdPrefix}-pause-cancel-button`}>
              Cancel
            </Button>
            <Button
              type="button"
              style={{ background: "#D97706", borderColor: "#D97706" }}
              disabled={isSaving || !pauseForm.reason || !pauseForm.effective_month}
              onClick={() => onConfirmPause(pauseForm)}
              data-testid={`${testIdPrefix}-pause-confirm-button`}
            >
              {isSaving ? "Pausing..." : "Pause project"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={archiveOpen} onOpenChange={onArchiveOpenChange}>
        <DialogContent className="sm:max-w-[560px]" data-testid={`${testIdPrefix}-archive-modal`}>
          <DialogHeader>
            <DialogTitle className="text-rose-700" data-testid={`${testIdPrefix}-archive-modal-title`}>Archive project</DialogTitle>
            <DialogDescription data-testid={`${testIdPrefix}-archive-modal-subtitle`}>
              {projectName || "Project"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="lifecycle-modal-impact-note archive" data-testid={`${testIdPrefix}-archive-modal-warning-note`}>
              <strong>This cannot be undone by a POD owner.</strong><br />
              Project removed from all active metrics. All data preserved — soft delete only.
            </div>

            <label className="field-label" data-testid={`${testIdPrefix}-archive-reason-label`}>Reason *</label>
            <select
              className="field-select"
              value={archiveForm.reason}
              onChange={(event) => setArchiveForm((prev) => ({ ...prev, reason: event.target.value }))}
              data-testid={`${testIdPrefix}-archive-reason-select`}
            >
              <option value="">Select reason...</option>
              {PROJECT_ARCHIVE_REASONS.map((reason) => (
                <option key={reason} value={reason}>{reason}</option>
              ))}
            </select>

            <label className="field-label" data-testid={`${testIdPrefix}-archive-confirm-name-label`}>Type project/client name to confirm *</label>
            <Input
              value={archiveForm.confirm_name}
              onChange={(event) => setArchiveForm((prev) => ({ ...prev, confirm_name: event.target.value }))}
              placeholder={clientName || projectName || "Type exact name"}
              data-testid={`${testIdPrefix}-archive-confirm-name-input`}
            />

            <label className="field-label" data-testid={`${testIdPrefix}-archive-effective-month-label`}>Apply in receivables from month *</label>
            <Input
              type="month"
              value={archiveForm.effective_month}
              onChange={(event) => setArchiveForm((prev) => ({ ...prev, effective_month: event.target.value }))}
              data-testid={`${testIdPrefix}-archive-effective-month-input`}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onArchiveOpenChange(false)} data-testid={`${testIdPrefix}-archive-cancel-button`}>
              Cancel
            </Button>
            <Button
              type="button"
              style={{
                background: archiveNameMatched ? "#B91C1C" : "#FEE2E2",
                borderColor: archiveNameMatched ? "#B91C1C" : "#FECACA",
                color: archiveNameMatched ? "#fff" : "#EF9A9A",
              }}
              disabled={isSaving || !archiveNameMatched || !archiveForm.effective_month}
              onClick={() => onConfirmArchive(archiveForm)}
              data-testid={`${testIdPrefix}-archive-confirm-button`}
            >
              {isSaving ? "Archiving..." : "Archive project"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
